import { Injectable } from "@angular/core";
import { FocusAreaService } from "@app/core/services/focus-areas/focus-area.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import {
  catchError,
  debounceTime,
  map,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";
import * as actions from "./actions";
import * as toast from "@app/core/components/toast/ngrx/toast.actions";
import { selectEntityIds } from "./selectors";
import { focusAreaFormatParameters } from "@app/settings/map-areas/focus-areas/focus-area-util";
import { SalesFocusFilter } from "@app/settings/map-areas/focus-areas";
import { ApiService } from "@app/core/services/api/api.service";
import { Employee } from "@app/models";

@Injectable()
export class SalesFocusAreasEffects {
  fetchSalesFocusAreas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchSalesFocusAreas),
      debounceTime(500),
      switchMap(({ officeIds, agentIds, entityType }) => {
        if (
          !entityType &&
          (!officeIds || officeIds?.length === 0) &&
          (!agentIds || agentIds?.length === 0)
        ) {
          return of(
            actions.fetchSalesFocusAreasSuccess({
              areas: [],
            })
          );
        }

        const params = focusAreaFormatParameters(
          officeIds,
          agentIds,
          entityType
        ) as {
          filters?: SalesFocusFilter[];
          nestedFilters?: Record<string, unknown>[];
          sort?: { field: string; order: "asc" | "desc" };
          pagination?: { offset: number; limit: number };
        };

        return this.service.getWithTotal(params).pipe(
          switchMap(({ total, areas }) => {
            if (total > 1000) {
              return this.service
                .get({
                  ...params,
                  pagination: {
                    offset: 1000,
                    limit: 1000,
                  },
                })
                .pipe(map((nextAreas) => [...areas, ...nextAreas]));
            }

            return of(areas);
          }),
          map((areas) =>
            actions.fetchSalesFocusAreasSuccess({
              areas,
            })
          ),
          catchError(() =>
            of(
              actions.fetchSalesFocusAreasFail({
                message: "could not fetch areas for offices",
              })
            )
          )
        );
      })
    )
  );

  searchSalesFocusAreas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.searchSalesFocusAreas),
      switchMap(({ address }) => {
        if (!address || address.trim().length <= 1) {
          return of(
            actions.searchSalesFocusAreasSuccess({
              areas: [],
            })
          );
        }

        const params = {
          filters: [
            {
              field: "address",
              operator: "eq",
              value: address,
            } as SalesFocusFilter,
          ],
        };

        return this.service.get(params).pipe(
          map((areas) =>
            actions.searchSalesFocusAreasSuccess({
              areas,
            })
          ),
          catchError(() =>
            of(
              actions.searchSalesFocusAreasFail({
                message: "could not fetch areas for this address",
              })
            )
          )
        );
      })
    )
  );

  refetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.refetch),
      withLatestFrom(this.store.pipe(select(selectEntityIds))),
      map(([, { agentIds, officeIds, entityType }]) =>
        actions.fetchSalesFocusAreas({ agentIds, officeIds, entityType })
      )
    )
  );

  renameArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.renameSalesFocusArea),
      switchMap(({ id, name, jsonData }) =>
        this.service.renameFocusArea(id, name, jsonData).pipe(
          switchMap((area) => [
            actions.renameSalesFocusAreaSuccess({ area }),
            toast.show({
              data: {
                message: "area_renamed",
                duration: 5000,
                type: "success",
              },
            }),
          ]),
          catchError(() => of(actions.renameSalesFocusAreaFail()))
        )
      )
    )
  );

  reassignArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.reassignSalesFocusArea),
      switchMap(({ id, officeId, agentId }) =>
        this.service.reassignFocusArea(id, officeId, agentId).pipe(
          map(() => actions.refetch()),
          catchError(() => of(actions.reassignSalesFocusAreaFail({ id })))
        )
      )
    )
  );

  saveSalesFocusArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveSalesFocusArea),
      switchMap(({ area }) =>
        this.service.updateFocusAreaGeometry(area).pipe(
          map(() => actions.saveSalesFocusAreaSuccess({ area })),
          catchError(() => of(actions.saveSalesFocusAreaFail()))
        )
      )
    )
  );

  saveSalesFocusAreaSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveSalesFocusAreaSuccess),
      switchMap(() => [
        actions.refetch(),
        toast.show({
          data: {
            message: "focus_area_saved",
            duration: 5000,
            type: "success",
          },
        }),
      ])
    )
  );

  saveSalesFocusAreaFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveSalesFocusAreaFail),
      switchMap(() => [
        toast.show({
          data: {
            message: "update_focus_area_failed",
            duration: 5000,
            type: "danger",
          },
        }),
      ])
    )
  );

  removeSalesFocusArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeSalesFocusArea),
      switchMap(({ id }) =>
        this.service.removeFocusArea(id).pipe(
          switchMap(() => [
            actions.refetch(),
            toast.show({
              data: {
                message: "focus_area_removed",
                duration: 5000,
                type: "success",
              },
            }),
          ]),
          // The reducer drops the area from state on the *request* action,
          // so a failed delete has to refetch or the area stays missing
          // from the list and map while still existing server-side.
          catchError(() => [
            actions.refetch(),
            toast.show({
              data: {
                message: "operation_failed",
                duration: 5000,
                type: "danger",
              },
            }),
          ])
        )
      )
    )
  );

  createSalesFocusArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createSalesFocusArea),
      switchMap(({ agent, office, name, geometry, jsonData }) =>
        this.service
          .createFocusArea(agent, office, geometry, name, jsonData)
          .pipe(
            switchMap((result) => [
              actions.createSalesFocusAreaSuccess({ area: result }),
              actions.refetch(),
              toast.show({
                data: {
                  message: "focus_area_created",
                  duration: 5000,
                  type: "success",
                },
              }),
            ]),
            catchError((error) =>
              of(
                actions.createSalesFocusAreaFail({
                  // The action contract is `error: string`; HttpErrorResponse
                  // is not an Error subclass, so read message defensively.
                  error: error?.message ?? String(error),
                })
              )
            )
          )
      )
    )
  );

  optimalNumbersInFocus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.optimalNumbersInFocusAreaRequest),
      switchMap(({ id, geometry }) =>
        this.service.getOptimalNumber(geometry).pipe(
          map((numberOfContacts) =>
            actions.optimalNumbersInFocusAreaSuccess({ id, numberOfContacts })
          ),
          catchError(() => of(actions.optimalNumbersInFocusAreaFail({ id })))
        )
      )
    )
  );

  fetchAllEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchAllEmployeesRequest),
      switchMap(({ eaOfficeId, isAdmin }) => {
        const params = {
          status: "active",
          includeOffice: true,
          limit: 1000,
          eaOfficeIds: isAdmin ? null : eaOfficeId,
        };

        return this.apiService.get("employees/legacy", params).pipe(
          map((response: { employees?: Employee[] }) => {
            let employees = Array.isArray(response?.employees)
              ? response.employees
              : [];
            if (employees.length > 1) {
              employees = employees
                .slice()
                .sort((a, b) =>
                  (a.employeeFullName ?? "").localeCompare(
                    b.employeeFullName ?? ""
                  )
                );
            }

            return actions.fetchAllEmployeesSuccess({ employees });
          }),
          catchError(() => of(actions.fetchAllEmployeesFail()))
        );
      })
    )
  );

  marketSharesInFocus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchFocusAreaMarketShareRequest),
      switchMap(({ id, polygons, start, end }) =>
        this.service.getMarketShare(polygons, start, end).pipe(
          map(({ areaShare, areaSales }) =>
            actions.fetchFocusAreaMarketShareSuccess({
              id,
              areaShare,
              areaSales,
            })
          ),
          catchError(() => of(actions.fetchFocusAreaMarketShareFail({ id })))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly service: FocusAreaService,
    private apiService: ApiService,
    private readonly store: Store
  ) {}
}
